import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, TrendingUp, MapPin, CheckCircle2, Eye, Calendar, Clock } from 'lucide-react';

const NearMissDashboard = () => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionTaken, setActionTaken] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, reportsRes] = await Promise.all([
        axiosInstance.get('/near-miss/analytics'),
        axiosInstance.get('/near-miss')
      ]);
      setAnalytics(analyticsRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      console.error('Failed to load near miss data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!actionTaken.trim()) {
      toast.error('Please describe the action taken');
      return;
    }

    setSubmitting(true);
    try {
      await axiosInstance.put(`/near-miss/${selectedReport.id}/acknowledge`, {
        actionTaken
      });
      toast.success('Near miss acknowledged and action recorded');
      setShowActionDialog(false);
      setActionTaken('');
      loadData();
    } catch (error) {
      console.error('Failed to acknowledge:', error);
      toast.error('Failed to record action');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredReports = reports.filter(report =>
    report.whatAlmostHappened.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.workerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'submitted':
        return <Badge variant="default" className="bg-yellow-600">Pending Review</Badge>;
      case 'acknowledged':
        return <Badge variant="default" className="bg-blue-600">Acknowledged</Badge>;
      case 'action_taken':
        return <Badge variant="default" className="bg-green-600">Action Taken</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Analytics Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{analytics?.total || 0}</div>
            <p className="text-xs text-gray-500 mt-1">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600">{analytics?.thisWeek || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{analytics?.thisMonth || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Pending Action</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">
              {analytics?.statusBreakdown?.submitted || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Needs review</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Locations */}
      {analytics?.topLocations && analytics.topLocations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-red-600" />
              Top Locations (Most Reports)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.topLocations.map((loc, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center font-bold text-red-600">
                      {index + 1}
                    </div>
                    <span className="font-medium">{loc.location}</span>
                  </div>
                  <Badge variant="destructive">{loc.count} reports</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Near Miss Reports</CardTitle>
              <CardDescription>Review and take action on reported near misses</CardDescription>
            </div>
            <Button onClick={loadData} variant="outline" size="sm">
              Refresh
            </Button>
          </div>
          <div className="mt-4">
            <Input
              placeholder="Search by description, location, or worker name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredReports.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No near miss reports found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredReports.map((report) => (
                <div
                  key={report.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(report.status)}
                        <span className="text-sm text-gray-500">
                          {new Date(report.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <h4 className="font-semibold text-lg text-gray-900 mb-1">
                        {report.whatAlmostHappened}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">{report.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {report.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {report.dateOccurred}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {report.timeOccurred}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm mt-2">
                        <p className="text-gray-700">
                          Reported by: <strong className="text-gray-900">{report.workerName}</strong>
                        </p>
                        <span className="text-gray-400">|</span>
                        <p className="text-gray-600">
                          Submitted: {new Date(report.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedReport(report)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                      {report.status === 'submitted' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            setSelectedReport(report);
                            setShowActionDialog(true);
                          }}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Take Action
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Linked JSAs/Audits */}
                  {(report.linkedJSAs?.length > 0 || report.linkedAudits?.length > 0) && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs font-medium text-gray-600 mb-2">
                        Linked Safety Documents:
                      </p>
                      <div className="flex gap-2">
                        {report.linkedJSAs?.length > 0 && (
                          <Badge variant="outline">
                            {report.linkedJSAs.length} JSAs at this location
                          </Badge>
                        )}
                        {report.linkedAudits?.length > 0 && (
                          <Badge variant="outline">
                            {report.linkedAudits.length} Recent audits
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Taken */}
                  {report.status === 'action_taken' && report.actionTaken && (
                    <div className="mt-3 pt-3 border-t bg-green-50 p-3 rounded">
                      <p className="text-sm font-medium text-green-900 mb-1">
                        Action Taken:
                      </p>
                      <p className="text-sm text-green-800">{report.actionTaken}</p>
                      {report.acknowledgedAt && (
                        <p className="text-xs text-green-600 mt-1">
                          Acknowledged on {new Date(report.acknowledgedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog open={!!selectedReport && !showActionDialog} onOpenChange={() => setSelectedReport(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedReport && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  Near Miss Report Details
                </DialogTitle>
                <DialogDescription>
                  Reported by {selectedReport.workerName} on {new Date(selectedReport.createdAt).toLocaleDateString()}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-yellow-900 mb-1">Reporter Information:</p>
                  <p className="text-sm text-yellow-800">
                    <strong>Name:</strong> {selectedReport.workerName}
                  </p>
                  <p className="text-sm text-yellow-800">
                    <strong>Submitted:</strong> {new Date(selectedReport.createdAt).toLocaleString()}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600 mb-1">What Almost Happened:</p>
                  <p className="font-semibold">{selectedReport.whatAlmostHappened}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Full Description:</p>
                  <p className="text-gray-900">{selectedReport.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Location:</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      {selectedReport.location}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Date & Time:</p>
                    <p className="font-medium">
                      {selectedReport.dateOccurred} at {selectedReport.timeOccurred}
                    </p>
                  </div>
                </div>
                {selectedReport.witnesses?.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Witnesses:</p>
                    <ul className="list-disc list-inside">
                      {selectedReport.witnesses.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Take Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Take Action on Near Miss</DialogTitle>
            <DialogDescription>
              Record what action was taken to address this near miss
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Describe the action taken (e.g., Added warning signs, Updated JSA, Scheduled training...)"
              value={actionTaken}
              onChange={(e) => setActionTaken(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAcknowledge}
              disabled={submitting || !actionTaken.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? 'Saving...' : 'Acknowledge & Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NearMissDashboard;
